import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { productList } from "../../services/Api";

export const fetchProducts = createAsyncThunk(
    'products/fetchProducts',
    async (params = {}, { getState }) => {
        try {
            const { filters } = getState().products;
            const requestParams = {
                sort: 'id',
                sort_order: filters.sorting || 'desc',
                page: filters.page,
                paginate_per_page: filters.paginate_per_page,
                ...(filters.search && { product_name: filters.search }),
                ...(filters.from && { from: filters.from }),
                ...(filters.to && { to: filters.to }),
                ...(filters.status && { status: filters.status }),
                ...(filters.company_id && { company_id: filters.company_id })
            };

            // Merge any additional params passed directly to the thunk
            const mergedParams = { ...requestParams, ...params };

            const response = await productList(mergedParams);
            return response;
        } catch (error) {
            throw error;
        }
    }
);

const initialState = {
    products: [],
    loading: false,
    error: null,
    shouldRefresh: false,
    pagination: {
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        perPage: 10
    },
    filters: {
        from: '',
        to: '',
        search: '',
        status: '',
        sorting: 'desc',
        page: "1",
        paginate_per_page: "10",
        company_id: null
    }
};

const productsSlice = createSlice({
    name: "products",
    initialState,
    reducers: {
        setShouldRefresh: (state, action) => {
            state.shouldRefresh = action.payload;
        },
        setFilters: (state, action) => {
            // If we're setting a company_id to null, reset all filters
            if (action.payload.hasOwnProperty('company_id') && action.payload.company_id === null) {
                state.filters = { ...initialState.filters };
            } else {
                state.filters = { ...state.filters, ...action.payload };
            }
        },
        resetFilters: (state, action) => {
            // If preserveCompanyId is true, keep the company ID when resetting
            const preserveCompanyId = action.payload?.preserveCompanyId === true;
            const companyId = preserveCompanyId ? state.filters.company_id : null;

            state.filters = {
                ...initialState.filters,
                ...(companyId ? { company_id: companyId } : {})
            };
        },
        // Use this action for a complete reset, including company ID
        resetAllFilters: (state) => {
            state.filters = { ...initialState.filters };
        },
        clearCompanyFilter: (state) => {
            state.filters = { ...initialState.filters };
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchProducts.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchProducts.fulfilled, (state, action) => {
                state.loading = false;
                state.products = action.payload.data || [];
                if (action.payload.meta) {
                    state.pagination = {
                        currentPage: action.payload.meta.current_page || 1,
                        totalPages: action.payload.meta.last_page || 1,
                        totalItems: action.payload.meta.total || 0,
                        perPage: action.payload.meta.per_page || 10
                    };
                }
            })
            .addCase(fetchProducts.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message;
                state.products = [];
            });
    }
});

export const { setFilters, resetFilters, setShouldRefresh, clearCompanyFilter, resetAllFilters } = productsSlice.actions;
export default productsSlice.reducer;